import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Utensils } from 'lucide-react'
import { toast } from 'sonner'
import { useTelegram } from '../../hooks/useTelegram'
import { api } from '../../services/api'
import { CuratorCommentThread, type CuratorCommentDto } from '../CuratorCommentThread'
import { MealItemsBreakdown, type MealItem } from '../MealItemsBreakdown'

const F = 'Roboto, sans-serif'

const MEAL_LABELS: Record<string, string> = {
  BREAKFAST: 'Завтрак',
  LUNCH: 'Обед',
  DINNER: 'Ужин',
  SNACK: 'Перекус',
}

const MEAL_COLORS: Record<string, string> = {
  BREAKFAST: '#FF9500',
  LUNCH: '#4A9FE5',
  DINNER: '#6C63FF',
  SNACK: '#AF52DE',
}

// Длина описания должна совпадать с zod-схемой `mealSchema` в
// packages/api/src/routes/diary.ts (description max 500).
const DESCRIPTION_MAX = 500

interface ExistingMeal {
  id: string
  description?: string | null
  calories?: number | null
  protein?: number | null
  fat?: number | null
  carbs?: number | null
  items?: MealItem[] | null
  comments?: CuratorCommentDto[]
}

interface Props {
  open: boolean
  onClose: () => void
  mealType: string
  diaryEntryId?: string | null
  date: string
  meal?: ExistingMeal | null
  /** Вызывается после успешной сдачи с результатом стрика (для поп-апа «ударный режим»). */
  onStreak?: (streak?: { advanced?: boolean; streakDays?: number } | null) => void
}

interface MacroValues {
  description: string
  calories: string
  protein: string
  fat: string
  carbs: string
}

type MacroKey = 'calories' | 'protein' | 'fat' | 'carbs'

const EMPTY: MacroValues = { description: '', calories: '', protein: '', fat: '', carbs: '' }

// Диапазоны должны совпадать с zod-схемой `mealSchema` в
// packages/api/src/routes/diary.ts (calories 0–10000, protein/fat 0–1000, carbs 0–2000).
const MACRO_FIELDS: { key: MacroKey; label: string; unit: string; placeholder: string; color: string; min: number; max: number }[] = [
  { key: 'calories', label: 'Калории', unit: 'kcal', placeholder: '450', color: '#4A9FE5', min: 0, max: 10000 },
  { key: 'protein', label: 'Белки', unit: 'г', placeholder: '30', color: '#34C759', min: 0, max: 1000 },
  { key: 'fat', label: 'Жиры', unit: 'г', placeholder: '15', color: '#FF9500', min: 0, max: 1000 },
  { key: 'carbs', label: 'Углеводы', unit: 'г', placeholder: '55', color: '#AF52DE', min: 0, max: 2000 },
]

// Разрешаем только цифры и одну точку/запятую (запятая → точка). До 5 знаков
// до разделителя (10000 ккал) и 1 знака после.
function sanitizeMacroInput(raw: string): string {
  const normalized = raw.replace(',', '.').replace(/[^\d.]/g, '')
  const firstDot = normalized.indexOf('.')
  const cleaned = firstDot === -1
    ? normalized
    : normalized.slice(0, firstDot + 1) + normalized.slice(firstDot + 1).replace(/\./g, '')
  const [intPart = '', decPart = ''] = cleaned.split('.')
  const trimmedInt = intPart.slice(0, 5)
  const trimmedDec = decPart.slice(0, 1)
  if (cleaned.includes('.')) return `${trimmedInt}.${trimmedDec}`
  return trimmedInt
}

export function AddMealSheet({ open, onClose, mealType, diaryEntryId, date, meal, onStreak }: Props) {
  const [vals, setVals] = useState<MacroValues>(EMPTY)
  const queryClient = useQueryClient()
  const { notificationHaptic } = useTelegram()

  const accentColor = MEAL_COLORS[mealType] || '#4A9FE5'
  const mealLabel = MEAL_LABELS[mealType] || mealType
  const isExisting = !!meal?.id

  useEffect(() => {
    if (!open) return
    if (meal) {
      setVals({
        description: meal.description ?? '',
        calories: meal.calories != null ? String(meal.calories) : '',
        protein: meal.protein != null ? String(meal.protein) : '',
        fat: meal.fat != null ? String(meal.fat) : '',
        carbs: meal.carbs != null ? String(meal.carbs) : '',
      })
    } else {
      setVals(EMPTY)
    }
  }, [open, meal])

  const errors = useMemo<Partial<Record<MacroKey | 'description', string>>>(() => {
    const out: Partial<Record<MacroKey | 'description', string>> = {}
    for (const f of MACRO_FIELDS) {
      const raw = vals[f.key].trim()
      if (raw === '') continue
      const v = parseFloat(raw)
      if (!Number.isFinite(v)) {
        out[f.key] = 'Введите число'
        continue
      }
      if (v < f.min || v > f.max) {
        out[f.key] = `${f.label}: допустимо ${f.min}–${f.max} ${f.unit}`
      }
    }
    if (vals.description.length > DESCRIPTION_MAX) {
      out.description = `Описание: не более ${DESCRIPTION_MAX} символов`
    }
    return out
  }, [vals])

  const errorList = useMemo(() => Object.values(errors).filter(Boolean) as string[], [errors])
  const hasErrors = errorList.length > 0

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (meal?.id) {
        // Edit: явно отправляем null при очищенном поле, чтобы стереть значение.
        return api.updateMeal(meal.id, {
          description: vals.description.trim() === '' ? null : vals.description,
          calories: vals.calories.trim() === '' ? null : parseFloat(vals.calories),
          protein: vals.protein.trim() === '' ? null : parseFloat(vals.protein),
          fat: vals.fat.trim() === '' ? null : parseFloat(vals.fat),
          carbs: vals.carbs.trim() === '' ? null : parseFloat(vals.carbs),
        })
      }
      let entryId: string
      if (diaryEntryId) {
        entryId = diaryEntryId
      } else {
        const created = await api.createDiaryEntry({ date })
        entryId = created.entry.id
      }
      return api.addMeal({
        diaryEntryId: entryId,
        mealType: mealType as 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK',
        description: vals.description || undefined,
        calories: vals.calories ? parseFloat(vals.calories) : undefined,
        protein: vals.protein ? parseFloat(vals.protein) : undefined,
        fat: vals.fat ? parseFloat(vals.fat) : undefined,
        carbs: vals.carbs ? parseFloat(vals.carbs) : undefined,
      })
    },
    onSuccess: (data: { streak?: { advanced?: boolean; streakDays?: number } | null }) => {
      notificationHaptic('success')
      toast.success(isExisting ? 'Изменения сохранены' : `${mealLabel} добавлен`)
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      onClose()
      // Новый приём пищи мог закрыть день стрика → поп-ап «ударный режим».
      if (data?.streak) onStreak?.(data.streak)
    },
    onError: (err: unknown) => {
      notificationHaptic('error')
      // Достаём конкретное поле из 400 Validation Error API,
      // чтобы пользователь видел, что именно вне допустимого диапазона.
      const anyErr = err as { response?: { data?: { details?: Array<{ field?: string; message?: string }> } } }
      const details = anyErr?.response?.data?.details
      if (details && details.length > 0) {
        const first = details[0]
        const field = MACRO_FIELDS.find((f) => f.key === first.field)
        if (field) {
          toast.error(`${field.label}: реалистичное значение ${field.min}–${field.max} ${field.unit}`)
          return
        }
        if (first.message) {
          toast.error(first.message)
          return
        }
      }
      toast.error('Не удалось сохранить')
    },
  })

  const hasAnyValue = vals.description.trim() !== '' || MACRO_FIELDS.some((f) => vals[f.key].trim() !== '')
  // В режиме редактирования разрешаем «Сохранить» даже если все поля пусты —
  // это означает «очистить ранее введённые значения».
  const canSave = (hasAnyValue || isExisting) && !hasErrors && !saveMutation.isPending

  const handleSave = () => {
    if (!isExisting && !hasAnyValue) return
    if (hasErrors) {
      notificationHaptic('error')
      toast.error(errorList[0])
      return
    }
    saveMutation.mutate()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 400,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="modal-meal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(30px)',
              borderTopLeftRadius: 26, borderTopRightRadius: 26,
              maxHeight: '85vh', overflowY: 'auto',
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Handle */}
            <SheetGrip onClose={onClose} />

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px 8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 12,
                  background: `${accentColor}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Utensils size={18} color={accentColor} />
                </div>
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C2D41', margin: 0 }}>{mealLabel}</h2>
                  <p style={{ fontSize: 12, color: '#9AA5B4', margin: 0 }}>
                    {isExisting ? 'Приём пищи' : 'Добавить приём пищи'}
                  </p>
                </div>
              </div>
              <button
                data-testid="btn-close-meal"
                onClick={onClose}
                style={{
                  background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%',
                  width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            <div style={{ padding: '8px 20px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Description */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41', display: 'block', marginBottom: 4 }}>
                  Описание
                </label>
                <textarea
                  data-testid="input-meal-description"
                  value={vals.description}
                  onChange={(e) => setVals((p) => ({ ...p, description: e.target.value.slice(0, DESCRIPTION_MAX) }))}
                  onFocus={(e) => {
                    const el = e.currentTarget
                    setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250)
                  }}
                  placeholder="Что вы ели?"
                  rows={2}
                  maxLength={DESCRIPTION_MAX}
                  aria-invalid={!!errors.description}
                  style={{
                    width: '100%', borderRadius: 12,
                    border: `1.5px solid ${errors.description ? '#FF3B30' : 'rgba(26,58,92,0.12)'}`,
                    background: 'rgba(255,255,255,0.85)',
                    padding: '10px 12px', fontSize: 15, color: '#1C2D41',
                    outline: 'none', resize: 'none', fontFamily: F,
                  }}
                />
                {errors.description && (
                  <div
                    data-testid="error-meal-description"
                    style={{ fontSize: 12, fontWeight: 500, color: '#FF3B30', marginTop: 4 }}
                  >
                    {errors.description}
                  </div>
                )}
              </div>

              {/* FatSecret per-product breakdown (read-only). The fields below
                  are the meal aggregate; here we show each product separately. */}
              {meal?.items && meal.items.length > 0 && (
                <div
                  data-testid="meal-fatsecret-items"
                  style={{
                    borderRadius: 12,
                    border: '1px solid rgba(26,58,92,0.10)',
                    background: 'rgba(255,255,255,0.6)',
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41', marginBottom: 6 }}>
                    🍴 Состав (FatSecret)
                  </div>
                  <MealItemsBreakdown items={meal.items} />
                </div>
              )}

              {/* KBJU fields */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#1C2D41', display: 'block', marginBottom: 6 }}>
                  КБЖУ{meal?.items && meal.items.length > 0 ? ' — итого за приём' : ''}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {MACRO_FIELDS.map((f) => {
                    const fieldError = errors[f.key]
                    return (
                      <div
                        key={f.key}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 4,
                          borderRadius: 12, padding: '8px 10px',
                          background: fieldError ? 'rgba(255,59,48,0.06)' : `${f.color}08`,
                          border: `1px solid ${fieldError ? 'rgba(255,59,48,0.3)' : f.color + '20'}`,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 8, height: 8, borderRadius: 4,
                            background: fieldError ? '#FF3B30' : f.color, flexShrink: 0,
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 10, color: '#9AA5B4', fontWeight: 500, display: 'block' }}>{f.label}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                data-testid={`input-meal-${f.key}`}
                                value={vals[f.key]}
                                onChange={(e) => setVals((p) => ({ ...p, [f.key]: sanitizeMacroInput(e.target.value) }))}
                                onFocus={(e) => {
                                  const el = e.currentTarget
                                  setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250)
                                }}
                                inputMode="decimal"
                                pattern="[0-9]*"
                                placeholder={f.placeholder}
                                aria-invalid={!!fieldError}
                                style={{
                                  flex: 1, height: 30, border: 'none', background: 'transparent',
                                  fontSize: 16, fontWeight: 700,
                                  color: fieldError ? '#FF3B30' : '#1C2D41',
                                  outline: 'none', fontFamily: F, width: '100%',
                                }}
                              />
                              <span style={{ fontSize: 12, color: '#9AA5B4', flexShrink: 0 }}>{f.unit}</span>
                            </div>
                          </div>
                        </div>
                        {fieldError && (
                          <div
                            data-testid={`error-meal-${f.key}`}
                            style={{ fontSize: 11, fontWeight: 500, color: '#FF3B30' }}
                          >
                            {fieldError}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Save button — visible both when creating and when editing */}
            <div style={{ padding: '0 20px' }}>
              <button
                data-testid="btn-save-meal"
                onClick={handleSave}
                disabled={!canSave}
                style={{
                  width: '100%', height: 52, borderRadius: 16, border: 'none',
                  background: canSave
                    ? `linear-gradient(135deg, ${accentColor}, ${accentColor}CC)`
                    : 'rgba(26,58,92,0.12)',
                  color: canSave ? 'white' : '#9AA5B4',
                  fontSize: 16, fontWeight: 700,
                  cursor: canSave ? 'pointer' : 'default',
                  fontFamily: F, opacity: saveMutation.isPending ? 0.6 : 1,
                }}
              >
                {saveMutation.isPending ? 'Сохранение...' : isExisting ? 'Сохранить' : 'Добавить'}
              </button>
            </div>

            {/* Curator comments on this meal */}
            {meal?.id && (
              <div style={{ padding: '12px 20px 0' }}>
                <CuratorCommentThread
                  targetType="DIARY_MEAL"
                  targetId={meal.id}
                  comments={meal.comments ?? []}
                  invalidateKeys={[['diary']]}
                  title="Комментарии куратора"
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
